import { buildProductMeshParamsFromState } from '@/application/product/buildProductMeshParams';
import { container as repositoryContainer } from '@/infrastructure/container';
import { productFileUrl } from '@/api/product.api';
import { createThreeAdapters, type TThreeAdapters, type TCreateThreeAdaptersParams } from '@/infrastructure/engine3d/createThreeAdapters';
import { Preview3DController } from '@/application/Preview3DController';
import { ref, type Ref } from 'vue';
import type { TProductMeshSources } from '@/application/product/buildProductMeshParams';

// Vue-side entry to the 3D preview. Pass canvas + params on first
// mount; no-arg calls return the current binding (sidecars). Returns
// null before the first mount.
//
// `ready` resolves once attach finishes; `isReady` is the reactive
// mirror the template can gate the canvas visibility on so no
// mesh-less flash is shown to the user.
//
// Adapter wiring lives in createThreeAdapters (infra). The composable
// owns the Vue-lifecycle cache + application-layer controller
// composition only — no concrete Three class instantiation here.

// The caller supplies the DOM, the viewport hint and the URL-typed product identifier; what the
// engine needs about the product is read here through the ports. `sources` defaults to the
// container and exists to be overridden by the cache-ownership tests.
type TUseEditor3DInitParams = Omit<TCreateThreeAdaptersParams, 'canvas' | 'meshParams'> & {
    sources?: TProductMeshSources;
    urlProductType: string;
};

type TEditor3DBinding = {
    controller: Preview3DController;
    isReady: Ref<boolean>;
    ready: Promise<void>;
};

type TCacheEntry = TEditor3DBinding & {
    adapters: TThreeAdapters;
    canvasElement: HTMLCanvasElement;
};

// WeakMap keyed by canvas → GC reclaims entries with their canvas.
const _entryByCanvas = new WeakMap<HTMLCanvasElement, TCacheEntry>();
// Most-recent entry — no-arg sidecar calls target it. Nulled on
// dispose of the owning mount.
let _currentEntry: TCacheEntry | null = null;

function useEditor3D(canvasElement?: HTMLCanvasElement | null, initParams?: TUseEditor3DInitParams): TEditor3DBinding | null {
    if (canvasElement && initParams) {
        const existing = _entryByCanvas.get(canvasElement);
        if (existing) {
            _currentEntry = existing;
            return { controller: existing.controller, isReady: existing.isReady, ready: existing.ready };
        }
        const { sources, urlProductType, ...adapterParams } = initParams;
        const meshParams = buildProductMeshParamsFromState(sources ?? _defaultSources(), { fileUrl: productFileUrl, urlProductType });
        if (!meshParams) {
            console.error('useEditor3D: no product loaded, the 3D preview cannot be built');
            return null;
        }
        const adapters = createThreeAdapters({ canvas: canvasElement, ...adapterParams, meshParams });
        const controller = new Preview3DController(adapters.engine, adapters.viewport, adapters.cameraController, adapters.foldable);
        const isReady = ref(false);
        const ready = adapters.ready.then(() => {
            isReady.value = true;
        });
        const entry: TCacheEntry = { adapters, canvasElement, controller, isReady, ready };
        _entryByCanvas.set(canvasElement, entry);
        _currentEntry = entry;
        return { controller, isReady, ready };
    }
    if (!_currentEntry) {
        return null;
    }
    return { controller: _currentEntry.controller, isReady: _currentEntry.isReady, ready: _currentEntry.ready };
}

function _defaultSources(): TProductMeshSources {
    return {
        cuttingShapeRepository: repositoryContainer.cuttingShapeRepository,
        productStateRepository: repositoryContainer.productStateRepository,
    };
}

function _disposeEditor3D(canvasElement?: HTMLCanvasElement | null): void {
    if (canvasElement) {
        const entry = _entryByCanvas.get(canvasElement);
        if (!entry) {
            return;
        }
        entry.controller.dispose();
        _entryByCanvas.delete(canvasElement);
        if (_currentEntry === entry) {
            _currentEntry = null;
        }
        return;
    }
    if (!_currentEntry) {
        return;
    }
    const entry = _currentEntry;
    entry.controller.dispose();
    _entryByCanvas.delete(entry.canvasElement);
    _currentEntry = null;
}

// Test-only reset.
function _resetEditor3DCacheForTests(): void {
    if (_currentEntry) {
        _currentEntry.controller.dispose();
        _entryByCanvas.delete(_currentEntry.canvasElement);
        _currentEntry = null;
    }
}

export { _disposeEditor3D, _resetEditor3DCacheForTests, useEditor3D };
